import { getCollections } from '@/services/data-sources/get-collections';
import { useNodeStore } from '@/stores/node-store';
import type { NodeParam } from '@/types/node-types';

const UNSTRUCTURED_FIELD_IDS = new Set(['collection_scope', 'collections']);
const DATASET_FIELD_IDS = new Set(['dataset_scope', 'datasets']);

function isUnstructuredField(fieldId: string): boolean {
  return UNSTRUCTURED_FIELD_IDS.has(fieldId);
}

export function isDatasetField(fieldId: string): boolean {
  return DATASET_FIELD_IDS.has(fieldId);
}

export async function fetchCollectionScopeOptions(
  fieldId: string,
): Promise<NodeParam['options']> {
  const isUnstructured = isUnstructuredField(fieldId);
  const collections = await getCollections(isUnstructured ? true : undefined);

  if ('detail' in collections) {
    throw new Error(collections.detail);
  }

  if (isUnstructured) {
    return collections
      .filter((col) => col.id)
      .map((col) => ({
        option_id: col.id,
        option_display_name: col.name,
        description: col.description ?? '',
      })) as unknown as NodeParam['options'];
  }

  // Dataset options must carry collection_id — it is persisted into the
  // selection value and the agent needs it to query the dataset. Mirrors the
  // backend option builder (_get_structured_options in collection_params.py).
  const datasetOptions: {
    option_id: string;
    option_display_name: string;
    description: string;
    collection_id: string;
  }[] = [];
  for (const col of collections) {
    if (col.data_type !== 'structured' || !col.id) continue;
    for (const ds of col.config?.datasets ?? []) {
      if (ds.id) {
        datasetOptions.push({
          option_id: ds.id,
          option_display_name: `${ds.name} (${col.name})`,
          description: ds.description || col.description || '',
          collection_id: col.id,
        });
      }
    }
  }
  return datasetOptions as unknown as NodeParam['options'];
}

/**
 * Reads fresh node state from the store, updates options for the given field,
 * prunes any stale selections that no longer exist in the new options, and
 * merges the fresh option data (display name, description, collection_id)
 * into the surviving selections so previously saved values are repaired.
 */
export function applyRefreshedOptions(
  nodeId: string,
  fieldId: string,
  nextOptions: NodeParam['options'],
): void {
  const { nodes, setNodes } = useNodeStore.getState();

  const optionsById = new Map(
    ((nextOptions ?? []) as unknown as { option_id: string }[]).map((o) => [
      o.option_id,
      o,
    ]),
  );

  const updatedNodes = nodes.map((node) => {
    if (node.id !== nodeId) return node;
    const updatedParams = node.data?.struct?.params?.map((param) => {
      if (param.field_id !== fieldId) return param;

      let updatedValue = param.value;
      if (Array.isArray(param.value)) {
        const refreshed = (param.value as unknown[])
          .map((item) => {
            const id =
              typeof item === 'string'
                ? item
                : typeof item === 'object' &&
                    item !== null &&
                    'option_id' in item
                  ? (item as { option_id: string }).option_id
                  : null;
            const fresh = id !== null ? optionsById.get(id) : undefined;
            if (!fresh) return null;
            return typeof item === 'object' && item !== null
              ? { ...item, ...fresh }
              : fresh;
          })
          .filter((item) => item !== null);
        updatedValue = refreshed as unknown as typeof param.value;
      }

      return { ...param, options: nextOptions ?? [], value: updatedValue };
    });
    return {
      ...node,
      data: {
        ...node.data,
        struct: {
          ...node.data?.struct,
          params: updatedParams,
        },
      },
    };
  });

  setNodes(updatedNodes);
}
